import { SITE_URL } from "@/lib/site-url";

/**
 * Rutas limpias (sin `?tab=`) de las secciones públicas del panel y sus
 * metadatos SEO / Open Graph. Es la única fuente de verdad para:
 *  - las rutas `src/routes/**` que renderizan cada sección,
 *  - la navegación por pestañas del panel público (`Dashboard`),
 *  - el `head()` SSR de cada ruta y las etiquetas que pinta React.
 */

export type PublicToolKey =
  | "home"
  | "agent"
  | "roadmap"
  | "calculator"
  | "kpis"
  | "premises"
  | "proposals"
  | "thermometer"
  | "grants"
  | "tips"
  | "cases"
  | "marketing"
  | "music"
  | "posts"
  | "scent";

/** Pestaña interna del Dashboard a la que corresponde cada sección. */
export const PUBLIC_TOOL_TAB: Record<PublicToolKey, string> = {
  home: "home",
  agent: "sara",
  roadmap: "roadmap",
  calculator: "calculator",
  kpis: "kpis",
  premises: "locations",
  proposals: "proposals",
  thermometer: "thermometer",
  grants: "grants",
  tips: "tips",
  cases: "casos",
  marketing: "marketing",
  music: "marketing",
  posts: "marketing",
  scent: "marketing",
};

export interface PublicToolMeta {
  pathEs: string;
  pathEn: string;
  title: { es: string; en: string };
  description: { es: string; en: string };
  h1: { es: string; en: string };
  /** Descripción específica para la tarjeta OG (WhatsApp/redes). */
  ogDescription?: { es: string; en: string };
  twitterTitle?: { es: string; en: string };
  twitterDescription?: { es: string; en: string };
  /** Ruta pública de la imagen OG 1200x630. */
  image: string;
  imageAlt: { es: string; en: string };
  keywords?: { es: string; en: string };
  /** Herramienta de cuenta: no debe competir en buscadores. */
  noindex?: boolean;
}

export const PUBLIC_TOOLS: Record<PublicToolKey, PublicToolMeta> = {
  home: {
    pathEs: "/",
    pathEn: "/en",
    title: {
      es: "Retail Lemon | Gestión para comercios a pie de calle",
      en: "Retail Lemon | Complete Retail Management & Support",
    },
    description: {
      es: "Panel 360°, hoja de ruta y Agente Retail IA para tu tienda a pie de calle. Básico 39 €, Club Pro 69 € y Avanzado 99 €/mes, sin permanencia.",
      en: "360° dashboard, roadmap and Retail AI Agent for your street-level shop. Basic €39, Club Pro €69 and Advanced €99/month, no commitment.",
    },
    h1: {
      es: "Incorpora la experiencia de un experto a tu día a día.",
      en: "Bring an expert's experience into your day-to-day.",
    },
    image: "/assets/og/og-home.jpg",
    imageAlt: {
      es: "Panel 360° de Retail Lemon en una tienda a pie de calle",
      en: "Retail Lemon 360° dashboard inside a high-street store",
    },
    keywords: {
      es: "panel 360 retail, agente retail ia, club pro retail, gestión tienda, hoja de ruta retail, subvenciones comercio",
      en: "360 retail panel, Retail AI Agent, club pro retail, store management, retail roadmap, retail grants",
    },
  },
  agent: {
    pathEs: "/agente-retail-ia",
    pathEn: "/en/retail-ai-agent",
    title: {
      es: "Agente Retail IA | Consulta para tu comercio | Retail Lemon",
      en: "Retail AI Agent | Consultation for your shop | Retail Lemon",
    },
    description: {
      es: "Primera consulta gratis. IA especializada en comercios a pie de calle.",
      en: "First consultation free. AI specialised in high-street shops.",
    },
    h1: {
      es: "Tu experto en operaciones retail disponible 24/7",
      en: "Your retail operations expert, available 24/7",
    },
    image: "/assets/og/og-agente-ia.jpg",
    imageAlt: {
      es: "Agente Retail IA de Retail Lemon resolviendo dudas desde el móvil",
      en: "Retail Lemon Retail AI Agent answering questions on a phone",
    },
    keywords: {
      es: "agente retail ia, asistente ia comercio, consultoría retail inmediata, ia para tiendas",
      en: "Retail AI Agent, ai assistant for shops, instant retail consulting, ai for stores",
    },
  },
  roadmap: {
    pathEs: "/hoja-de-ruta",
    pathEn: "/en/retail-roadmap",
    title: {
      es: "Hoja de ruta para tu comercio | Retail Lemon",
      en: "Roadmap for your shop | Retail Lemon",
    },
    description: {
      es: "El orden de trabajo del local: qué atacar esta semana.",
      en: "The working order for your shop: what to tackle this week.",
    },
    h1: {
      es: "Tu plan estratégico personalizado para hacer crecer tu negocio",
      en: "Your personalised strategic plan to grow your business",
    },
    image: "/assets/og/og-hoja-de-ruta.jpg",
    imageAlt: {
      es: "Hoja de ruta por etapas para comercios en Retail Lemon",
      en: "Stage-by-stage retail roadmap in Retail Lemon",
    },
    keywords: {
      es: "hoja de ruta retail, plan estratégico tienda, checklist comercio, crecimiento negocio local",
      en: "retail roadmap, store strategic plan, retail checklist, local business growth",
    },
  },
  calculator: {
    pathEs: "/herramientas/calculadora",
    pathEn: "/en/tools/calculator",
    title: {
      es: "Calculadora de rentabilidad retail | Retail Lemon",
      en: "Retail Profitability & Margin Calculator | Retail Lemon",
    },
    description: {
      es: "Calcula gratis el punto de equilibrio, margen comercial y viabilidad financiera de tu comercio o local a pie de calle.",
      en: "Free online calculator to measure break-even point, retail profit margin, and financial viability for your shop.",
    },
    h1: {
      es: "Calculadora de rentabilidad y costes para comercio local",
      en: "Profitability and cost calculator for local retail",
    },
    ogDescription: {
      es: "¿Es tu tienda realmente rentable? Analiza tus márgenes y costes fijos en 2 minutos con la Calculadora Retail.",
      en: "Is your shop really profitable? Analyse your margins and fixed costs in 2 minutes with the Retail Calculator.",
    },
    twitterTitle: {
      es: "Calculadora de Rentabilidad Retail",
      en: "Retail Profitability Calculator",
    },
    twitterDescription: {
      es: "Analiza tus márgenes y costes fijos en 2 minutos con Retail Lemon.",
      en: "Analyse your margins and fixed costs in 2 minutes with Retail Lemon.",
    },
    image: "/assets/og/og-calculadora.jpg",
    imageAlt: {
      es: "Calculadora de rentabilidad retail de Retail Lemon",
      en: "Retail Lemon profitability calculator",
    },
    keywords: {
      es: "calculadora rentabilidad retail, margen comercial, punto de equilibrio tienda, viabilidad local comercial",
      en: "retail profitability calculator, retail margin, store break-even, shop viability",
    },
  },
  kpis: {
    pathEs: "/herramientas/kpis",
    pathEn: "/en/tools/kpis",
    title: {
      es: "KPIs para comercios | Retail Lemon",
      en: "KPIs for local shops | Retail Lemon",
    },
    description: {
      es: "Aprende las herramientas básicas para llevar bien tu negocio y entiende qué mirar cada mes según tu tipo de comercio.",
      en: "Learn the basic tools to run your business well and understand what to watch each month for your type of shop.",
    },
    h1: {
      es: "KPIs para llevar mejor tu negocio",
      en: "KPIs to run your business better",
    },
    ogDescription: {
      es: "Te enseñamos las herramientas básicas para llevar bien tu negocio, adaptadas a tu tipo de local.",
      en: "We teach you the basic tools to run your business well, adapted to your type of premises.",
    },
    image: "/assets/og/og-kpis.jpg",
    imageAlt: {
      es: "Herramienta de KPIs para comercios en Retail Lemon",
      en: "Retail Lemon KPI tool for local shops",
    },
    keywords: {
      es: "kpis comercio, indicadores tienda, control mensual negocio, kpis retail",
      en: "retail kpis, shop indicators, monthly business tracking, retail metrics",
    },
    noindex: true,
  },
  premises: {
    pathEs: "/herramientas/mis-locales",
    pathEn: "/en/tools/my-premises",
    title: {
      es: "Diagnóstico de locales comerciales | Retail Lemon",
      en: "Store Premises & Location Manager | Retail Lemon",
    },
    description: {
      es: "Analiza la ubicación, tráfico peatonal y rendimiento comercial de tus puntos de venta existentes o futuras aperturas.",
      en: "Analyze foot traffic, location potential, and commercial performance for your retail store locations.",
    },
    h1: {
      es: "Gestión y análisis estratégico de tus puntos de venta",
      en: "Strategic management and analysis of your points of sale",
    },
    image: "/assets/og/og-premises.jpg",
    imageAlt: {
      es: "Mapa de locales comerciales analizados con Retail Lemon",
      en: "Map of retail premises analysed with Retail Lemon",
    },
    keywords: {
      es: "gestor de locales, análisis ubicación tienda, tráfico peatonal, expansión retail",
      en: "store location manager, shop location analysis, foot traffic, retail expansion",
    },
    noindex: true,
  },
  proposals: {
    pathEs: "/herramientas/proveedores",
    pathEn: "/en/tools/suppliers",
    title: {
      es: "Proveedores para tu Comercio | Retail Lemon",
      en: "Suppliers for your Store | Retail Lemon",
    },
    description: {
      es: "Te proponemos proveedores con los que ya trabajamos, según lo que tu negocio esté buscando. Tú eliges. Exclusivo Club Pro.",
      en: "We suggest suppliers we already work with, based on what your business is looking for. You choose. Club Pro only.",
    },
    h1: {
      es: "Proveedores para tu comercio",
      en: "Suppliers for your store",
    },
    image: "/assets/og/og-suppliers.jpg",
    imageAlt: {
      es: "Proveedores para comercios en Retail Lemon",
      en: "Retail Lemon suppliers for local shops",
    },
    keywords: {
      es: "proveedores comercio, proveedores retail, reforma local, rotulación tienda, escaparatismo",
      en: "retail suppliers, shop suppliers, shop refit, store signage, window display",
    },
  },
  thermometer: {
    pathEs: "/detector-de-fugas",
    pathEn: "/en/leak-detector",
    title: {
      es: "Detector de Fugas Completo para comercios | Retail Lemon",
      en: "Complete Leak Detector for local shops | Retail Lemon",
    },
    description: {
      es: "Diagnóstico gratis de entrada: alquiler, personal, género, stock, equipo y ventas. Para tiendas, bares, cafeterías y locales. Resultado a fondo en Club Pro.",
      en: "Free entry diagnosis: rent, staff, goods, stock, team and sales. For shops, bars, cafés and premises. In-depth result in Club Pro.",
    },
    h1: {
      es: "Detector de Fugas Completo",
      en: "Complete Leak Detector",
    },
    ogDescription: {
      es: "¿El alquiler o el personal se comen el mes? Mide las fugas de tu local. Retail Lemon.",
      en: "Is rent or staff eating your month? Measure your premises' leaks. Retail Lemon.",
    },
    image: "/assets/og/og-leaks.jpg",
    imageAlt: {
      es: "Detector de Fugas Completo para comercios a pie de calle — Retail Lemon",
      en: "Complete Leak Detector for high-street businesses — Retail Lemon",
    },
    keywords: {
      es: "detector de fugas comercio, diagnóstico retail gratis, alquiler porcentaje facturación, coste personal hostelería, food cost, rotación stock",
      en: "shop leak detector, free retail diagnosis, rent share of revenue, hospitality staff cost, food cost, stock rotation",
    },
  },
  tips: {
    pathEs: "/consejos",
    pathEn: "/en/tips",
    title: {
      es: "Consejos e ideas para tu comercio | Retail Lemon",
      en: "Tips and ideas for your shop | Retail Lemon",
    },
    description: {
      es: "Consejos e ideas para el día a día del local: compras, equipo, caja y clientes.",
      en: "Tips and ideas for the day-to-day of your shop: buying, team, cash and customers.",
    },
    h1: {
      es: "Consejos e ideas para el día a día del local",
      en: "Tips and ideas for your shop's day-to-day",
    },
    image: "/assets/og/og-home.jpg",
    imageAlt: {
      es: "Consejos e ideas para comercios de Retail Lemon",
      en: "Retail Lemon tips and ideas for local shops",
    },
    keywords: {
      es: "consejos comercio local, ideas para tiendas, gestión diaria tienda, trucos hostelería",
      en: "local shop tips, ideas for stores, daily store management, hospitality tips",
    },
  },
  cases: {
    pathEs: "/casos-reales",
    pathEn: "/en/real-cases",
    title: {
      es: "Casos reales de comercios y hostelería | Retail Lemon",
      en: "Real cases from shops and hospitality | Retail Lemon",
    },
    description: {
      es: "Casos reales de tiendas, bares y restaurantes: el problema, lo que hicimos y el resultado.",
      en: "Real cases from shops, bars and restaurants: the problem, what we did and the result.",
    },
    h1: {
      es: "Casos reales de comercios y locales",
      en: "Real cases from shops and premises",
    },
    image: "/assets/og/og-home.jpg",
    imageAlt: {
      es: "Casos reales de comercios acompañados por Retail Lemon",
      en: "Real retail cases supported by Retail Lemon",
    },
    keywords: {
      es: "casos reales comercio, casos retail, ejemplos tiendas, casos hostelería",
      en: "real retail cases, retail case studies, shop examples, hospitality cases",
    },
  },
  grants: {
    pathEs: "/recursos/subvenciones",
    pathEn: "/en/resources/subsidies",
    title: {
      es: "Subvenciones para comercio y hostelería | Retail Lemon",
      en: "Retail Grants & Subsidies Finder | Retail Lemon",
    },
    description: {
      es: "Encuentra ayudas públicas, subvenciones de digitalización y fondos para el comercio minorista y restauración.",
      en: "Discover government grants, funding, and digitization subsidies tailored for retail businesses.",
    },
    h1: {
      es: "Subvenciones y financiación para tu comercio local",
      en: "Grants and funding for your local business",
    },
    image: "/assets/og/og-grants.jpg",
    imageAlt: {
      es: "Buscador de subvenciones para comercios de Retail Lemon",
      en: "Retail Lemon grants finder for local shops",
    },
    keywords: {
      es: "subvenciones comercio, ayudas retail, subvenciones digitalización, fondos restauración, buscador ayudas",
      en: "retail grants, shop subsidies, digitization grants, hospitality funding, grants finder",
    },
  },
  marketing: {
    pathEs: "/marketing",
    pathEn: "/en/marketing",
    title: { es: "Marketing para comercios | Retail Lemon", en: "Marketing for local shops | Retail Lemon" },
    description: { es: "Publicaciones, música y aroma para mejorar la experiencia de tu local.", en: "Social posts, music and scent to improve your store's experience." },
    h1: { es: "Marketing", en: "Marketing" },
    image: "/assets/og/og-music.jpg",
    imageAlt: { es: "Marketing para comercios con Retail Lemon", en: "Marketing for local shops with Retail Lemon" },
    keywords: { es: "música para tiendas, marketing para comercios, ambiente de tienda", en: "music for stores, marketing for local shops, store atmosphere" },
  },
  music: {
    pathEs: "/musica-para-tu-local",
    pathEn: "/en/music-for-your-store",
    title: { es: "Música para tu local | Música legal para comercios y hostelería | Retail Lemon", en: "Music for your store | Legal music for retail and hospitality | Retail Lemon" },
    description: {
      es: "Música ambiental y legal para tiendas, bares, cafeterías y restaurantes. Canales gratuitos con prueba diaria y licencia comercial por superficie con Retail Lemon.",
      en: "Legal background music for shops, bars, cafés and restaurants. Free channels with a daily trial and a commercial licence priced by floor area, with Retail Lemon.",
    },
    h1: { es: "Música para tu local: música legal y ambiental para comercios y hostelería", en: "Music for your store: legal background music for retail and hospitality" },
    ogDescription: {
      es: "Música de fondo legal para tu tienda, bar o cafetería. Sin Spotify ni YouTube en la tienda: canales listos y licencia por superficie.",
      en: "Legal background music for your shop, bar or café. No Spotify or YouTube in store: ready-made channels and a licence priced by floor area.",
    },
    image: "/assets/og/og-music.jpg",
    imageAlt: { es: "Música ambiental para comercios y hostelería con Retail Lemon", en: "Background music for retail and hospitality with Retail Lemon" },
    keywords: {
      es: "música para tiendas, música ambiental para comercios, música legal para negocios, música para hostelería, hilo musical tienda, música de fondo bares y cafeterías",
      en: "music for stores, background music for shops, legal music for businesses, music for hospitality, in-store music, background music for bars and cafés",
    },
  },
  posts: {
    pathEs: "/publicaciones-para-tu-local",
    pathEn: "/en/social-posts-for-your-store",
    title: { es: "Publicaciones para tu local | Contenido y redes para comercios | Retail Lemon", en: "Social posts for your store | Content and social media for retail | Retail Lemon" },
    description: {
      es: "Publicaciones en redes sociales para tu tienda, bar o restaurante: contenido mensual adaptado a tu local. Pack opcional para miembros del Club Pro.",
      en: "Social media posts for your shop, bar or restaurant: monthly content tailored to your business. Optional pack for Club Pro members.",
    },
    h1: { es: "Publicaciones en redes para tu local: contenido listo para comercios y hostelería", en: "Social posts for your store: ready-made content for retail and hospitality" },
    image: "/assets/og/og-posts.jpg",
    imageAlt: { es: "Publicaciones en redes para comercios con Retail Lemon", en: "Social media posts for local shops with Retail Lemon" },
    keywords: {
      es: "publicaciones redes sociales comercios, contenido para tiendas, gestión redes sociales negocio local, marketing de contenidos retail",
      en: "social media posts for shops, content for stores, social media management for local business, retail content marketing",
    },
  },
  scent: {
    pathEs: "/aroma-para-tu-local",
    pathEn: "/en/scent-for-your-store",
    title: { es: "Aroma para tu local | Ambientación para comercios y hostelería | Retail Lemon", en: "Scent for your store | Ambient scenting for retail and hospitality | Retail Lemon" },
    description: {
      es: "Aromatización y ambientación para tiendas, bares y cafeterías: mejora la experiencia de tu local y que te recuerden. Pack opcional para miembros del Club Pro.",
      en: "Scenting and ambience for shops, bars and cafés: improve your store's experience and be remembered. Optional pack for Club Pro members.",
    },
    h1: { es: "Aroma para tu local: ambientación para comercios y hostelería", en: "Scent for your store: ambient scenting for retail and hospitality" },
    image: "/assets/og/og-scent.jpg",
    imageAlt: { es: "Aromatización de locales comerciales y hostelería con Retail Lemon", en: "Scenting for retail and hospitality premises with Retail Lemon" },
    keywords: {
      es: "aroma para tiendas, aromatización de locales, aroma marketing retail, ambientación para comercios, aroma para hostelería",
      en: "scent for stores, scent marketing retail, ambient scenting for shops, scent for hospitality, store fragrance",
    },
  },
};

export const publicToolPath = (key: PublicToolKey, lang: "es" | "en") =>
  lang === "es" ? PUBLIC_TOOLS[key].pathEs : PUBLIC_TOOLS[key].pathEn;

/** Clave de sección a partir de la pestaña interna del Dashboard (o null). */
export const publicToolKeyForTab = (tab: string): PublicToolKey | null => {
  const entry = (Object.keys(PUBLIC_TOOL_TAB) as PublicToolKey[]).find(
    (k) => PUBLIC_TOOL_TAB[k] === tab,
  );
  return entry ?? null;
};

/** Metadatos SSR (`head()`) de una sección pública. */
export function publicToolHead(key: PublicToolKey, lang: "es" | "en") {
  const m = PUBLIC_TOOLS[key];
  const path = lang === "es" ? m.pathEs : m.pathEn;
  const url = `${SITE_URL}${path}`;

  // Fuente única de title/description/og:*/twitter:*/robots/keywords:
  // el componente <SEO /> (Helmet, también en SSR) que renderiza Index.
  // Aquí solo se añaden las etiquetas complementarias que <SEO /> no emite,
  // para no duplicar ninguna en el HTML servido.
  const meta: Array<Record<string, string>> = [
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "twitter:url", content: url },
  ];

  // Canonical + hreflang los emite <SEO /> (Helmet) desde Index. No se
  // declaran aquí: los <link> del head() son propiedad de React y useSEO los
  // eliminaría del DOM, provocando un crash al cambiar de ruta.
  return { meta };
}
